import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type {
  PricingTableRow as IPricingTableRow,
  PricingTableRowValue,
  PricingTableColumn,
  PricingTableCurrencySettings,
  PricingTableSummaryValues,
  PricingTableSectionSummaryValues,
  ExtendedSummary,
} from '../../types/pricing-table';
import {
  PricingTableColumn as EPricingTableColumn,
  NumberInputType,
} from '../../types/enums/pricing-table';
import { getColumnNumberInputType } from './pricing-table-column.helper';

export const getProductName = (row: IPricingTableRow, columns: PricingTableColumn[]): string => {
  const nameColumn = columns.find(col => col.name === EPricingTableColumn.Name);
  return row.values?.find(val => val.columnId === nameColumn?.id)?.value || '';
};

export const getColumnDisplayName = (columnId: string, columns: PricingTableColumn[]): string => {
  const column = columns.find(col => col.id === columnId);
  return column?.displayName || t('unknown');
};

const formatValueForAria = (
  rawValue: string,
  numberInputType: NumberInputType,
  columnName?: string,
  isFlatFee?: boolean
): string => {
  if (
    numberInputType === NumberInputType.Currency ||
    numberInputType === NumberInputType.Percentage ||
    numberInputType === NumberInputType.Number
  ) {
    const numericValue = rawValue || '0';
    if (numberInputType === NumberInputType.Currency) {
      return numericValue;
    } else if (numberInputType === NumberInputType.Percentage) {
      return `${numericValue}%`;
    } else {
      if (columnName === EPricingTableColumn.Discount && isFlatFee === false) {
        return `${numericValue}%`;
      }
      return numericValue;
    }
  } else if (rawValue === '') {
    return t('empty');
  } else {
    return rawValue;
  }
};

export const getBasicAriaLabel = (
  rowValue: PricingTableRowValue,
  columns: PricingTableColumn[],
  currencySettings: PricingTableCurrencySettings | null
): string => {
  const displayName = getColumnDisplayName(rowValue.columnId, columns);
  const rawValue = rowValue.value || '';
  const currency = currencySettings?.formatOptions?.currency || '';

  const column = columns.find(col => col.id === rowValue.columnId);
  const numberInputType = getColumnNumberInputType(column);

  const valueForAria = formatValueForAria(rawValue, numberInputType, currency);

  const currencyText =
    numberInputType === NumberInputType.Currency && currency ? ` ${currency}` : '';

  return `${displayName}: ${valueForAria}${currencyText}`;
};

export const getDiscountAriaLabel = (
  rowValue: PricingTableRowValue,
  columns: PricingTableColumn[],
  currencySettings: PricingTableCurrencySettings | null,
  row: IPricingTableRow
): string => {
  const displayName = getColumnDisplayName(rowValue.columnId, columns);
  const rawValue = rowValue.value || '';
  const currency = currencySettings?.formatOptions?.currency || '';
  const productName = getProductName(row, columns);
  const column = columns.find(col => col.id === rowValue.columnId);
  const numberInputType = getColumnNumberInputType(column);

  if (column?.name === EPricingTableColumn.Discount) {
    const isFlatFee = row.discountFlatFee;
    if (rawValue === '') {
      if (!isFlatFee) {
        return `${productName}: 0% ${t('discount')}`;
      }
      return `${displayName}: ${t('empty')}`;
    }

    if (isFlatFee) {
      const valueForAria = formatValueForAria(rawValue, numberInputType, currency);
      return t('discount_flat_fee_discount', {
        product: productName,
        value: valueForAria,
        currency,
      });
    } else {
      const percentageValue = `${rawValue || '0'}%`;
      return `${productName}: ${percentageValue} ${t('discount')}`;
    }
  }

  const valueForAria = formatValueForAria(rawValue, numberInputType, currency);
  const currencyText = currency ? ` ${currency}` : '';
  return `${displayName}: ${valueForAria}${currencyText}`;
};

export const getOptionalProductAriaLabel = (
  rowValue: PricingTableRowValue,
  columns: PricingTableColumn[],
  currencySettings: PricingTableCurrencySettings | null,
  hasOptionalProductDeselected: boolean
): string => {
  const displayName = getColumnDisplayName(rowValue.columnId, columns);
  const rawValue = rowValue.value || '';
  const currency = currencySettings?.formatOptions?.currency || '';

  const column = columns.find(col => col.id === rowValue.columnId);
  const numberInputType = getColumnNumberInputType(column);

  const valueForAria = formatValueForAria(rawValue, numberInputType, currency);

  const currencyText =
    numberInputType === NumberInputType.Currency && currency ? ` ${currency}` : '';

  let label = `${displayName}: ${valueForAria}${currencyText}`;

  if (hasOptionalProductDeselected) {
    label += ` ${t('optional_product_deselected')}`;
  }

  return label;
};

export const getQuantityChangeAriaLabel = (
  row: IPricingTableRow,
  columns: PricingTableColumn[]
): string => {
  const productName = getProductName(row, columns);
  return t('change_quantity_for', { product: productName });
};

export const getOptionalProductCheckboxAriaLabel = (
  row: IPricingTableRow,
  columns: PricingTableColumn[]
): string => {
  const productName = getProductName(row, columns);
  return t('optional_product', { product: productName });
};

export const getTotalPriceAriaLabel = (
  row: IPricingTableRow,
  columns: PricingTableColumn[],
  currencySettings: PricingTableCurrencySettings | null,
  totalPrice: string,
  hasOptionalProductDeselected: boolean
): string => {
  const productName = getProductName(row, columns);
  const currency = currencySettings?.formatOptions?.currency || '';

  const discountColumn = columns.find(col => col.name === EPricingTableColumn.Discount);
  const discountValue = row.values?.find(val => val.columnId === discountColumn?.id);
  const hasDiscount = discountValue?.value && discountValue.value !== '';

  let label = t('total_price_for', { product: productName, value: totalPrice, currency });

  if (hasDiscount) {
    const discountAmount = discountValue.value;
    const isFlatFee = row.discountFlatFee;

    if (isFlatFee) {
      label += ` ${t('after_flat_fee_discount', { value: discountAmount, currency })}`;
    } else {
      label += ` ${t('after_percentage_discount', { value: discountAmount })}`;
    }
  }

  if (hasOptionalProductDeselected) {
    label += ` ${t('optional_product_deselected')}`;
  }

  return label;
};

export const getNetPriceAriaLabel = (
  row: IPricingTableRow,
  columns: PricingTableColumn[],
  currencySettings: PricingTableCurrencySettings | null,
  netPrice: string,
  hasOptionalProductDeselected: boolean
): string => {
  const productName = getProductName(row, columns);
  const currency = currencySettings?.formatOptions?.currency || '';

  const discountColumn = columns.find(col => col.name === EPricingTableColumn.Discount);
  const discountValue = row.values?.find(val => val.columnId === discountColumn?.id);
  const hasDiscount = discountValue?.value && discountValue.value !== '';

  if (!hasDiscount && !hasOptionalProductDeselected) {
    return t('net_price_for', { product: productName, value: netPrice, currency });
  }

  let label = t('net_price_for', { product: productName, value: netPrice, currency });

  if (hasDiscount) {
    const discountAmount = discountValue.value;
    const isFlatFee = row.discountFlatFee;

    if (isFlatFee) {
      label += ` ${t('after_flat_fee_discount', { value: discountAmount, currency })}`;
    } else {
      label += ` ${t('after_percentage_discount', { value: discountAmount })}`;
    }
  }

  if (hasOptionalProductDeselected) {
    label += ` ${t('optional_product_deselected')}`;
  }

  return label;
};

export const getRowAriaLabel = (
  row: IPricingTableRow,
  columns: PricingTableColumn[],
  hasOptionalProductDeselected: boolean
): string => {
  const productName = getProductName(row, columns);
  const status = hasOptionalProductDeselected ? 'deselected' : 'included';
  return t('pricing_row_for', { product: productName, status });
};

const addDiscountContext = (
  label: string,
  columnName: string,
  rawValue: string,
  isFlatFee: boolean
): string => {
  if (columnName === EPricingTableColumn.Discount && rawValue !== '') {
    const discountType = isFlatFee ? t('flat_fee') : t('percentage');
    return `${label} ${discountType}`;
  }
  return label;
};

const addProductContext = (label: string, columnName: string, productName: string): string => {
  const keyColumns = [
    EPricingTableColumn.Units,
    EPricingTableColumn.Discount,
    EPricingTableColumn.Tax,
    EPricingTableColumn.Price,
  ];

  if (keyColumns.includes(columnName as EPricingTableColumn)) {
    return `${productName} - ${label}`;
  }
  return label;
};

const buildComprehensiveLabel = (
  displayName: string,
  valueForAria: string,
  currencyText: string,
  columnName: string,
  rawValue: string,
  isFlatFee: boolean,
  hasOptionalProductDeselected: boolean,
  productName: string
): string => {
  const standardColumns = [
    EPricingTableColumn.Name,
    EPricingTableColumn.Price,
    EPricingTableColumn.Units,
    EPricingTableColumn.Tax,
    EPricingTableColumn.TotalPrice,
    EPricingTableColumn.NetPrice,
    EPricingTableColumn.Discount,
    EPricingTableColumn.Description,
    EPricingTableColumn.Sku,
  ];

  const isStandardColumn = standardColumns.includes(columnName as EPricingTableColumn);

  if (!isStandardColumn) {
    let label = `${valueForAria}${currencyText}`;

    if (hasOptionalProductDeselected) {
      label += ` ${t('optional_product_deselected')}`;
    }

    return label;
  }

  let label = `${displayName}: ${valueForAria}${currencyText}`;

  label = addDiscountContext(label, columnName, rawValue, isFlatFee);

  if (hasOptionalProductDeselected) {
    label += ` ${t('optional_product_deselected')}`;
  }

  label = addProductContext(label, columnName, productName);

  return label;
};

export const getComprehensiveAriaLabel = (
  rowValue: PricingTableRowValue,
  columns: PricingTableColumn[],
  currencySettings: PricingTableCurrencySettings | null,
  row: IPricingTableRow,
  hasOptionalProductDeselected: boolean
): string => {
  const displayName = getColumnDisplayName(rowValue.columnId, columns);
  const rawValue = rowValue.value || '';
  const currency = currencySettings?.formatOptions?.currency || '';
  const productName = getProductName(row, columns);

  const column = columns.find(col => col.id === rowValue.columnId);
  const columnName = column?.name || '';
  const numberInputType = getColumnNumberInputType(column);

  const valueForAria = formatValueForAria(
    rawValue,
    numberInputType,
    columnName,
    row.discountFlatFee
  );

  const currencyText =
    numberInputType === NumberInputType.Currency && currency ? ` ${currency}` : '';

  return buildComprehensiveLabel(
    displayName,
    valueForAria,
    currencyText,
    columnName,
    rawValue,
    row.discountFlatFee || false,
    hasOptionalProductDeselected,
    productName
  );
};

const getGenericAriaLabel = (
  displayName: string,
  rawValue: string,
  currency: string,
  includeCurrency: boolean = true
): string => {
  const currencyText = includeCurrency && currency ? ` ${currency}` : '';
  return `${displayName}: ${rawValue}${currencyText}`;
};

export const getSummaryAriaLabel = (summary: ExtendedSummary, currency: string): string => {
  const displayName = summary.name || summary.displayName || '';
  const rawValue = summary.value || '0';
  const includeCurrency = summary.unit === 'Currency' && !!currency;

  return getGenericAriaLabel(displayName, rawValue, currency, includeCurrency);
};

export const getSectionDiscountAriaLabel = (
  sectionSummary: PricingTableSectionSummaryValues,
  currency: string
): string => {
  const displayName = sectionSummary.discount?.displayName || '';
  const rawValue = sectionSummary.discount?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getSectionTaxAriaLabel = (
  sectionSummary: PricingTableSectionSummaryValues,
  currency: string
): string => {
  const displayName = sectionSummary.tax?.displayName || '';
  const rawValue = sectionSummary.tax?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getSectionSubtotalAriaLabel = (
  sectionSummary: PricingTableSectionSummaryValues,
  currency: string
): string => {
  const displayName = sectionSummary.price?.displayName || '';
  const rawValue = sectionSummary.price?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getBaseSubtotalAriaLabel = (
  summary: PricingTableSummaryValues,
  currency: string
): string => {
  const displayName = summary.baseSubtotal?.displayName || '';
  const rawValue = summary.baseSubtotal?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getTaxBreakdownTotalAriaLabel = (
  summary: PricingTableSummaryValues,
  currency: string
): string => {
  const displayName = summary.taxBreakdown?.displayName || '';
  const rawValue = summary.taxBreakdown?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getTaxRateAriaLabel = (
  taxRate: { displayName?: string; value?: string },
  currency: string
): string => {
  const displayName = taxRate.displayName || '';
  const rawValue = taxRate.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getCombinedSubtotalAriaLabel = (
  summary: PricingTableSummaryValues,
  currency: string
): string => {
  const displayName = summary.combinedSubtotal?.displayName || '';
  const rawValue = summary.combinedSubtotal?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getTotalDiscountAriaLabel = (
  summary: PricingTableSummaryValues,
  currency: string
): string => {
  const displayName = summary.discount?.displayName || '';
  const rawValue = summary.discount?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getTotalTaxAriaLabel = (
  summary: PricingTableSummaryValues,
  currency: string
): string => {
  const displayName = summary.tax?.displayName || '';
  const rawValue = summary.tax?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getSummaryTotalPriceAriaLabel = (
  summary: PricingTableSummaryValues,
  currency: string
): string => {
  const displayName = summary.price?.displayName || '';
  const rawValue = summary.price?.value || '0';

  return getGenericAriaLabel(displayName, rawValue, currency);
};

export const getTablePriceAriaLabel = (
  table: { displayName?: string; name?: string; value?: string },
  currency: string
): string => {
  const tableName = table.displayName || table.name || 'Untitled';
  const rawValue = table.value || '0';

  return `${tableName}: ${rawValue} ${currency}`;
};

export const getTotalPriceAriaLabelForSummary = (
  totalPrice: string | number,
  currency: string
): string => {
  const rawValue = totalPrice.toString();

  return t('total_sum_value_currency', { value: rawValue, currency });
};

export const getEmptyTotalPriceAriaLabel = (currency: string = ''): string =>
  t('total_sum_zero', { currency });

export const createBaseSubtotalAriaLabel = (
  summary: PricingTableSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getBaseSubtotalAriaLabel(summary, currency);
};

export const createTaxBreakdownTotalAriaLabel = (
  summary: PricingTableSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getTaxBreakdownTotalAriaLabel(summary, currency);
};

export const createCombinedSubtotalAriaLabel = (
  summary: PricingTableSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getCombinedSubtotalAriaLabel(summary, currency);
};

export const createTotalDiscountAriaLabel = (
  summary: PricingTableSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getTotalDiscountAriaLabel(summary, currency);
};

export const createTotalTaxAriaLabel = (
  summary: PricingTableSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getTotalTaxAriaLabel(summary, currency);
};

export const createTotalPriceAriaLabel = (
  summary: PricingTableSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getSummaryTotalPriceAriaLabel(summary, currency);
};

export const createSummaryAriaLabel = (
  summary: ExtendedSummary,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getSummaryAriaLabel(summary, currency);
};

export const createDiscountAriaLabel = (
  sectionSummary: PricingTableSectionSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getSectionDiscountAriaLabel(sectionSummary, currency);
};

export const createTaxAriaLabel = (
  sectionSummary: PricingTableSectionSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getSectionTaxAriaLabel(sectionSummary, currency);
};

export const createSubtotalAriaLabel = (
  sectionSummary: PricingTableSectionSummaryValues,
  pricingTable: { currencySettings?: PricingTableCurrencySettings }
) => {
  const currency = pricingTable?.currencySettings?.formatOptions?.currency || '';
  return getSectionSubtotalAriaLabel(sectionSummary, currency);
};

export const createTablePriceAriaLabel = (table: {
  displayName?: string;
  name?: string;
  value?: string;
  currencySettings?: PricingTableCurrencySettings;
}) => {
  const currency = table?.currencySettings?.formatOptions?.currency || '';
  return getTablePriceAriaLabel(table, currency);
};

export const createTotalPriceAriaLabelForSummary = (
  totalPrice: string | number,
  currencySettings: PricingTableCurrencySettings
) => {
  const currency = currencySettings?.formatOptions?.currency || '';
  return getTotalPriceAriaLabelForSummary(totalPrice, currency);
};
